// src/pages/Help/data/helpData.js

// Import all function icons
import subcodonIcon from "../../../assets/images/eirna/functions/single_tran_icon.png";
import comparisonIcon from "../../../assets/images/eirna/functions/comparison_icon.png";
import dataQualityIcon from "../../../assets/images/eirna/functions/metainfo_icon.png";
import differentialIcon from "../../../assets/images/eirna/functions/diff_icon.png";
import pausePredictionIcon from "../../../assets/images/eirna/functions/pausepred_icon.png";
import genomeBrowserIcon from "../../../assets/images/eirna/functions/single_genome_icon.png";
import singleGenomeIcon from "../../../assets/images/eirna/functions/single_genome_icon.png";
import comparisonGenomeIcon from "../../../assets/images/eirna/functions/comparison_genome_icon.png";
import orfDetectionIcon from "../../../assets/images/eirna/functions/orf_detection_icon.png";
import differentialDistributionIcon from "../../../assets/images/eirna/functions/diff_silhouette_icon.png";
import differentialSilhouetteIcon from "../../../assets/images/eirna/functions/diff_silhouette_icon.png";
import timeSeriesIcon from "../../../assets/images/eirna/functions/time_series_icon.png";
import codonOccupancyIcon from "../../../assets/images/eirna/functions/codon_occupancy_icon.png";
import transcriptomeInfoIcon from "../../../assets/images/eirna/functions/traninfo_icon.png";
import seriesDataIcon from "../../../assets/images/eirna/functions/time_series_icon.png";
import readthroughIcon from "../../../assets/images/eirna/functions/rrts_icon.png";
import polysomeIcon from "../../../assets/images/eirna/functions/poly_heatmap_icon.png";
import batchSubcodonIcon from "../../../assets/images/eirna/functions/batch_icon.png";
import batchComparisonIcon from "../../../assets/images/eirna/functions/batch_comparison_icon.png";
import diffLeaderCdsIcon from "../../../assets/images/eirna/functions/diff_uorf_icon.png";
import orfTranslationIcon from "../../../assets/images/eirna/functions/orf_detection_icon.png";
import coverageExplanation from "../../../assets/images/eirna/functions/coverage_explanation.png";
import readthroughAnalysisIcon from "../../../assets/images/eirna/functions/rrts_icon.png";
import rrtsIcon from "../../../assets/images/eirna/functions/rrts_icon.png";
import utrPeriodicityIcon from "../../../assets/images/eirna/functions/utr_periodicity_icon.png";
import terminationMetageneIcon from "../../../assets/images/eirna/functions/termination_metagene_icon.png";

// Import help desk image
import helpDeskImage from "../../../assets/images/eirna/quickstart/quickstart.png";

export const quickStartData = {
  title: "Quickstart Help Page",
  headerImage: helpDeskImage,
  description:
    "This Is A Basic Image Based Walkthrough Of The Some Of Main Features Of EIRNABio Connect Using An Example Dataset.",
  subtitle: "Visualising An Individual Gene/Transcript.",
  content:
    "From The Homepage Click On A Project. On The Plot Selection Page Click 'Sub-Codon Position Profile' In The Top Left. You Will See The Page Shown In The Screenshot Below:",
};

export const helpSections = {
  faqs: [
    {
      question:
        "How do I download raw counts?",
      answer: `Raw counts can be downloaded from the data quality page. Navigate to that page and select the 
               'Counts table' option at the top of the page. The options section will allow you to choose 
               betweeen gene level or transcript level counts. You can also choose whether or not to aggregate
               counts from all selected files. By default the results will contain raw counts, if transript level
               counts are selected the count type can also be set to TPM or RPKM.`,
      expanded: false,
    },
    {
      question:
        "What are the codes that appear in the titles of EIRNABio Connect plots?",
      answer: `These are unique codes that are generated for every plot on EIRNABio Connect. These allow for quickly sharing plots and record the specific files/studies used to generate the plot as well as any settings that were changed.<br><br>
      By following a link in the form https://eirnabioconnect.com/short/short_code the plot can be recreated in the browser. For example if the code in the title of the plot was (l2I) following the link <a href="https://eirnabioconnect.com/short/l2I" target="_blank">https://eirnabioconnect.com/short/l2I</a> would load the plot in the browser.<br><br>
      Scrolling up the page would show you which settings and files were used to generate the plot. This link can be sent to others to facilitate easily sharing plots. Note, following a link to a short code will result in an error page if you are not logged in or you do not have permission to access that project.`,
      expanded: false,
    },
    {
      question: "How do I share plots?",
      answer: `For every plot generated on EIRNABio Connect a unique id is also generated and displayed in the plots title. A link can be generated using this unique id in the format https://eirnabioconnect.com/short/unique_code.<br><br>
      Sending this link to someone will recreate the plot on their browser using the same files/settings/organism used to create the plot. For convenience, a button will appear above the plot which will be labelled "Direct link to this plot". Right clicking on the button and clicking "copy link location" allows you to easily send the full link to others.`,
      expanded: false,
    },
    {
      question: "How is the Ribo-Seq/mRNA-Seq data processed?",
      answer: `For the majority of Ribo-seq projects unique molecular identifiers (UMIs) are used which allow PCR duplicates to be collapsed. We always communicate the details regarding the UMIs and sequence read architecture by email. Aside from UMIs, the typical data processing pipeline is the following: adapter sequences are removed using <a href="http://cutadapt.readthedocs.io/en/stable/guide.html" target="_blank">cutadapt</a>. Reads below 20 nucleotides in length after adapter removal are discarded.<br><br>
      Reads are aligned to rRNA and tRNA sequences using <a href="http://bowtie-bio.sourceforge.net/index.shtml" target="_blank">bowtie</a>. Reads that map to rRNA or tRNA are discarded while remaining reads are aligned to the transcriptome using <a href="http://bowtie-bio.sourceforge.net/index.shtml" target="_blank">bowtie</a>. UMI's are removed where applicable. The resulting BAM file is parsed with a python script. An offset is also determined for every read by choosing the highest peak upstream of the start codon using the counts from the metagene profile.`,
      expanded: false,
    },
    {
      question:
        "What is the difference between transcript level and gene level counts?",
      answer: `<b>Transcript level counts:</b> the number of reads that align to that particular transcript. Depending on the plot type these may be broken down into reads aligning to the 5' Leader (5' UTR), CDS, and 3' trailer (3'UTR) or a total for the entire transcript. Reads that align to more than one transcript from different genes or that align to multiple positions on the same transcript are classed as ambiguous.<br><br>
      <b>Gene level counts:</b> an aggregate of all counts for transcripts of that gene. A particular read that aligns to multiple transcripts will only be counted once. Reads are categorized in a hierarchical way giving priority to CDS regions, then 5' Leader (5'UTR) or 3' trailer (3' UTR) regions and finally non-coding regions.`,
      expanded: false,
    },
    {
      question: "How long does it usually take for a plot to complete?",
      answer: `It's difficult to give expected times for plots as it depends heavily on the options and files selected as well as the current computational load on the server. With default options, here are some rough estimates for how long each plot will take to complete:<br><br>
      Subcodon profiles, comparison profiles and most of the data quality plots typically complete very quickly (a few seconds at most), the main exception here is the counts table, if transcript level counts are requested this can take several minutes to return.`,
      expanded: false,
    },
    {
      question:
        "Why does the reading frame of the annotated CDS change on subcodon/comparison plots?",
      answer: `Which reading frame the annotated CDS ends up in is dictated by the length of the 5' Leader/5' UTR for the transcript in question. The first nucleotide of the transcript is always denoted as frame 1, the second nucleotide frame 2, the third frame 3 and then it repeats from there.<br><br>
      So if the 5' Leader length in nucleotides is divisible by 3 then the CDS will be in reading frame 1, but otherwise will end up in frame 2 or frame 3.`,
      expanded: false,
    },
  ],
  plots: [
    {
      id: "subcodon-profile",
      title: "Sub-codon profile help",
      description:
        `This plot can be used to view data aligned to specific transcripts, seq types
         like Ribo-seq and Disome-seq will be colored according to reading frame.`,
      image: subcodonIcon,
      plotDescription: `

      <div class="help_video_reminder">
        This page uses checkbox file inputs, it's recommended you view the chapter on 'Checkbox file selection' in the
        <a href="/help/plot/help-video?t=0:55" target="_blank">help video</a> if this is your first time using this 
        plot/analysis type.<br><br>

        This page generates an interactive plot, it's recommended you view the chapter on 'Interacting with plots' in 
        the <a href="/help/plot/help-video?t=4:44" target="_blank">help video</a> if this is your first time using this plot/analysis type. 
      </div>

      <p class="help_subheader">Why use subcodon profiles</p>

      <p>Sub-codon profiles allow you to visualize not just which regions of a transcript
         have a high density of Ribo-seq reads, but also which reading frame they are 
         biased toward. This can be very useful in cases of atypical translation events
         such as translated upstream (uORF) or downstream (dORF) open reading frames, 
         cases of readthrough or frameshifting and n-terminal extensions. In cases where
         ORFs are overlapping such as with overlapping uORFs/ overlapping dORFs and 
         nested ORFs this is even more important.</p>

      <p>The ability to distinguish which frames are being translated is heavily 
         influenced by the periodicity of the Ribo-seq data. The stronger the 
         periodicity, the easier it will be to determine which frames are being translated.</p>

      <p>Click here to view the subcodon profile for a transcript belonging to the gene 
         B2M, this transcript has a relatively clean profile with Ribo-seq reads 
         contained almost entirely within the CDS in the expected frame and very few 
         Ribo-seq reads contained in the 5'UTR or 3'UTR. Within the CDS region, note 
         that most read counts map to the reads within the red reading frame or Frame 1.
         This suggests that translation occurs in Frame 1 which correlates with the 
         available annotation from GENCODE.</p>

      <p>Click here to view the sub-codon profile for transcript ENST0000323146 
         (SLC35A4), a transcript with a translated uORF. Upstream of the CDS region, 
         starting at nucleotide position 18 and ending at nucleotide position 327, the 
         mapped ribosomal footprints support the translation of an uORF arising from 
         Frame 2 (green). Looking at the read count table, further supports this with 
         4355 reads mapping to Frame 2 in the 5’ UTR compared to 1577 reads in Frame 3 
         and 882 reads in Frame 1.</p>

      <p class="help_subheader">General Plot Structure</p>

      <p>The plot title is the gene name with the shortcode in brackets.
         The coding region of the gene (if applicable) is denoted by two CDS markers 
         (solid vertical black bars), indicating the CDS start and CDS stop positions, 
         which can be toggled on or off by clicking the corresponding entry in the 
         legend.</p>

      <p>Exon junctions (dotted vertical black bars) can similarly be toggled on or 
         off by clicking ‘Exon junctions’ in the legend.
         The blue merged CDS track denotes any annotated coding regions which overlap 
         with this transcript, i.e if there is a blue bar in the merged CDS track in 
         the 5’ Leader of this transcript this means the CDS of another transcript 
         is overlapping it. Below this are tracks representing the three possible 
         reading frames - with each frame color coded red, green or blue; white bars 
         indicate start codons and black bars indicate stop codons.</p>

      <p>The X-axis denotes the nucleotide position on the selected transcript, while 
         the Y-axis denotes the read count. The transcript ID and transcript length 
         (in nucleotides) are shown below the plot. Just above this, the nucleotide 
         sequence track is displayed, which appears as a multicolored line at lower zoom
         levels and expands to show individual nucleotides when zoomed in. The colours 
         of each letter indicate which reading frame the codon is found in, i.e if you 
         are looking at the codon ‘ATG’ and the first letter is red, then you know that
         the ATG codon is in the first reading frame.</p>


      <p>The main part of the plot is where reads aligned to this transcript are 
         displayed. All samples will be aggregated according to their seq-type. Seq 
         types that have subcodon resolution, e.g Ribo-seq/Disome-seq, will be coloured
         according to which reading frame the inferred a-site is found in, this 
         corresponds to the colours of the open reading frame architecture below the plot.</p>

      <p>If the annotated CDS of a particular transcript is in say reading frame 3 
         (blue), typically you will see a much higher density of blue reads in the 
         main plot in this region compared to red or green. Reads from RNA-seq and 
         other seq types will appear as a single grey line in the main part of the plot.</p>


      <p>To the right of the plot are navigation and image download tools. Next are 
         tabs to share the plot via shortcode, view the region in the genome browser, 
         or download the read counts for the transcript. The ‘Show ambig’ option enables
         visualisation of ambiguously mapped reads, which is disabled by default to 
         improve specificity, while ‘Hide sequence’ removes the nucleotide sequence track.
         Below these tabs is a table showing the total number of reads mapped to the 
         transcript by reading frame which is further categorised by 5' Leader (5'UTR), 
         CDS or 3' Trailer (3'UTR). RNA counts will also be displayed if RNA-seq data is
         selected. These counts will automatically update to include ambiguous counts 
         if the ‘Show Ambig’ button is pressed.</p> 

    
  `,
    },
    {
      id: "comparison-plot",
      title: "Comparison plot help",
      description:
        "This plot can be used to compare data from two or more conditions on a single transcript.",
      image: comparisonIcon,
      plotDescription: `

      <div class="help_video_reminder">
        This page uses drag and drop file inputs, it's recommended you view the chapter on 'Drag and drop file selection' in the
        <a href="/help/plot/help-video?t=2:23" target="_blank">help video</a> if this is your first time using this 
        plot/analysis type.<br><br>

        This page generates an interactive plot, it's recommended you view the chapter on 'Interacting with plots' in 
        the <a href="/help/plot/help-video?t=4:44" target="_blank">help video</a> if this is your first time using this plot/analysis type. 
      </div>

      <p class="help_subheader">Why use comparison plots</p>

      <p>Comparison plots allow you to directly compare ribosome footprint density across
         transcripts between experimental conditions or groups. By visualising differences
         in ribo-seq signal, and optionally RNA-seq signal, comparison plots help 
         distinguish changes in translation from changes in transcript abundance. 
         This makes them particularly useful for identifying condition-specific 
         translational regulation, stress responses, and treatment-induced effects.</p>

      <p>Click <a href="https://eirnabioconnect.com/short/l2J" target="_blank">here</a> to view the comparison plot for the
         transcript ENST00000309268 for the gene EEF1A1 between two groups from the 
         DemoArsenite study.</p>


      <p>By default ribosome coverage is enabled, which adds a count at every position a
         particular read covers, rather than just the inferred A-site, typically this 
         makes interpreting differences between conditions easier. Here, Group 1 represents control 
         (untreated) samples while Group 2 represents HEK cells treated with arsenite.</p>

      <p>The plot demonstrates a higher density of ribosome footprints along the coding 
         region of EEF1A1 in the control group. This indicates that the translation of 
         EEF1A1 is reduced in the arsenite-treated samples.</p> 

      <p>Click <a href="https://eirnabioconnect.com/short/l2K" target="_blank">here</a> to view the comparison plot for 
         the same transcript with RNAseq data also enabled.
         </p> 

      <p>Additional groups can be added via the ‘+ Add Group’ button and selecting the 
         corresponding RNA-seq samples.</p>

      <p>In this plot, the difference in ribosome footprints between the control and 
         arsenite-treated groups is again observed. However, the plot demonstrates 
         similar RNA-seq profiles between the two groups, suggesting that the observed 
         change in translation is not driven by changes in transcript abundance but 
         instead translational efficiency.</p>

      <p class="help_subheader">General Plot Structure</p>

      <p>The plot title is the gene name with the shortcode in brackets.
         The coding region of the gene (if applicable) is denoted by two CDS markers 
         (solid vertical black bars), indicating the CDS start and CDS stop positions, 
         which can be toggled on or off by clicking the corresponding entry in the 
         legend. Exon junctions (dotted vertical black bars) can similarly be toggled on
         or off by clicking ‘Exon junctions’ in the legend.</p>

      <p>The blue merged CDS track denotes any annotated coding regions which overlap 
         with this transcript, i.e if there is a blue bar in the merged CDS track in 
         the 5’ Leader of this transcript this means the CDS of another transcript is 
         overlapping it. Below this are tracks representing the three possible reading 
         frames; white bars indicate start codons and black bars indicate stop codons.</p>

      <p>The X-axis denotes the nucleotide position on the selected transcript, while 
         the Y-axis denotes the read count. The transcript ID and transcript length 
         (in nucleotides) are shown below the plot. Just above this, the nucleotide 
         sequence track is displayed, which appears as a black line at lower zoom 
         levels and expands to show individual nucleotides when zoomed in.</p>

      <p>To the right of the plot are navigation and image download tools. Next are tabs
         to share the plot via shortcode, view the region in the genome browser, or 
         download the read counts for the transcript. The ‘Show ambig’ option enables 
         visualisation of ambiguously mapped reads, which is disabled by default to 
         improve specificity, while ‘Hide sequence’ removes the nucleotide sequence track.</p>

      <p>Below these tabs is a table showing the total number of reads mapped to the 
         transcript for each group in the comparison. These counts will automatically 
         update to include ambiguous counts if the ‘Show Ambig’ button is pressed.</p> 


       <p class="help_subheader">How does normalisation work on comparison plots?</p>
        There are several normalisation options available for the comparison plot but the
        default method 'By mapped reads per seq type' works by first determining the total
        number of mapped reads for each distinct seq type for each group. It then 
        calculates a scaling factor relative to the group with the lowest number of 
        mapped reads of that seq-type. Read counts at each position in groups with 
        higher coverage are scaled down by this factor. For example, if there is one 
        ribo-seq dataset in condition 1 with 1,000,000 mapped reads 
        and one ribo-seq dataset in condition 2 with 500,000 mapped reads, condition 1 will be 
        scaled by a factor of 0.5 so that both conditions are effectively compared at 
        the same sequencing depth.
        Normalisation can be adjusted via the ‘Advances Settings’ tab.
        The normalisation methods are:
        None - no normalisation of profile counts
        By mapped reads per seq type - normalise profile counts by total reads mapped for each sequence type independently
        By mapped reads ignoring seq type - normalise profile counts by total reads mapped ignoring sequencing type
        By reads mapped to the transcripts - normalise profile counts by number of reads mapped to the transcript

  `,
    },
    {
      id: "data-quality",
      title: "Data Quality help",
      description:
        "This page can be used to retrieve raw counts and produce various quality metric plots such as read length distribution, triplet periodicity and metagene plots among others.",
      image: dataQualityIcon,
      plotDescription: `

    <div class="help_video_reminder">
        This page uses checkbox file inputs, it's recommended you view the chapter on 'Checkbox file selection' in the
        <a href="/help/plot/help-video?t=0:55" target="_blank">help video</a> if this is your first time using this 
        plot/analysis type.<br><br>

        This page generates both interactive plots and tables, it's recommended you view the chapter on 'Interacting with plots'
        and 'Interacting with tables' in the <a href="/help/plot/help-video?t=4:44" target="_blank">help video</a> if this is your first
        time using this plot/analysis type. 
    </div>

    <p class="help_subheader">Example plots</p>
    <p>Click <a href="https://eirnabioconnect.com/short/l2L" target="_blank">here</a> to view an aggregated triplet periodicity plot.</p>
    <p>Click <a href="https://eirnabioconnect.com/short/l2M" target="_blank">here</a> to view a panel of triplet periodicity plots.</p>
    <p>Click <a href="https://eirnabioconnect.com/short/l2N" target="_blank">here</a> to view a metagene plot centered around start codons.</p>
    <p>Click <a href="https://eirnabioconnect.com/short/l2O" target="_blank">here</a> to view a metagene plot centered around stop codons.</p>
    <p>Click <a href="https://eirnabioconnect.com/short/l2P" target="_blank">here</a> to view the origin of mapped reads plot.</p>

    <p class="help_subheader">Triplet Periodicity</p>
    <p>This plot shows the triplet periodicity across a given range of readlengths. The user may decide the minimum and maximum readlengths in the settings for this plot, but typically the default range of 25 - 35 is sufficient for Ribo-seq reads.</p>
    <p>Each readlength will have three bars associated with it corresponding to one of three frames the reads align to. A particular read length is considered to have strong triplet periodicity if the count in one of the frames is significantly higher than the other two frames. The legend in the top right will also show a score based on the periodicity of most frequent read length.</p>
    <p>The score is calculated using the formula: 1-(count of the second highest peak/count of the highest peak). This will result in readlengths with strong periodicity receiving a score close to 1, while readlengths with weak periodicity receive a score close to 0.</p>
    <p>The better the triplet periodicity for a given study the clearer it will be which frame is being translated when viewing reads from this dataset on the subcodon profile plot.</p>

    <p class="help_subheader">Metagene Profile</p>
    <p>This plot shows counts on the y-axis and a position relative to some sequence feature on the x-axis, this can be the annotated start or stop codon.</p>
    <p>All reads from any transcript that align near the chosen position will be plotted, by default the position of the inferred A-site will be plotted, but the position of the 5' ends of reads can also be plotted by turning off the 'apply offsets' option.</p>
    <p>Genuine good quality Ribo-Seq data should have a much higher density of reads downstream of the start codon than upstream of the start codon (and vice versa for the stop codon).</p>
    <p>Typically the default options are sufficient and should not need to be changed in most cases, with the exception of the 'center around' option, to change between start/stop codon metagenes.</p>


    <p class="help_subheader">Custom Metagene</p>
    <p>This plot is similar to the metagene plot, but allows you to build a metagene around a custom list of sequences on the fly.</p>
    <p>To use simply input a sequence or comma separated list of sequences into the 'Custom sequence list' input box and then optionally limit where to search the transcripts for that sequence.</p>
    <p>For example, to make a stop codon metagene profile centered around UGA stops only, you would put UGA in the 'custom sequence list' input box.</p>
    <p>This will make a metagene around every UGA codon on the transcript though, so you will want to limit this to only the last 3 nucleotides of the transcript</p>
    <p>To do this just set both the min/max limit anchor to 'CDS stop', then set the min limit offset to -3 and the max limit offset to 0.</p>
    <p>This can be used to limit the search to other regions on the transcript too, for example to search only 5' Leaders/5' UTR's, set the min limit anchor to 
    'Transcript start' and the max limit anchor to 'Transcript end', leaving the offsets for both at 0 </p>
    <p>Custom sequence search is done on the fly so will take longer than the annotated start/stop codon plots which are pre-computed.</p>
    <p>Note: the following IUPAC codes are also recognised in the custom sequence search:</p>
    <p><strong>R</strong>: A/G<br><strong>Y</strong>: C/U<br><strong>S</strong>: G/C<br><strong>W</strong>: A/U<br><strong>K</strong>: G/U<br><strong>M</strong>: A/C<br><strong>B</strong>: C/G/U<br><strong>D</strong>: A/G/U<br><strong>H</strong>: A/C/U<br><strong>V</strong>: A/C/G<br><strong>N</strong>: A/C/G/U</p>

    <p class="help_subheader">Counts table</p>
    <p>This is used to get Ribo-Seq or RNA-Seq counts for genes or transcripts. Counts can be taken from just Ribo-Seq files or just RNA-Seq files, or both.</p>
    <p>For performance reasons only the top 1000 genes are shown, but clicking the link just above and to the left of the table allows you to download all rows in csv format.</p>
    <p>The 'View Plot' link in the table will bring you to the sub codon profile of that gene/transcript and plot the data from the selected files. If only Ribo-Seq or only RNA-Seq files are selected results will be given broken down by region. If both Ribo-Seq and RNA-Seq files are selected (and the aggregate option is turned on) the resulting table will have one column for Ribo-Seq count (based on CDS), one column for RNA-Seq count (based on entire gene/transcript) and a column for Translation Efficiency (TE) which is the Ribo-Seq count divided by the RNA-Seq count (a pseudo count of 1 is added to all RNA-Seq counts to avoid divide by zero errors).</p>
    <p>Transcript level counts are simply the number of reads that align to that particular transcript. Depending on the plot type these may be broken down into reads aligning to the 5' Leader (5' UTR), CDS, and 3' trailer (3'UTR) or a total for the entire transcript. Currently only unambiguous counts are shown in the counts table if transcript level counts are selected.</p>
    <p>Gene level counts will aggregate all counts for transcripts of that gene. A particular read that aligns to multiple transcripts will only be counted once. Reads are categorized in a hierarchical way giving priority to CDS regions, then 5' Leader (5'UTR) or 3' trailer (3' UTR) regions and finally non-coding regions. For example if a read aligns to 2 transcripts and in one transcript it's found in a CDS region and in another it's found in the 5' Leader it will be classed as a CDS read. Weighted ambiguous counts are included for all gene level counts in the counts table.</p>

    <p class="help_subheader">Origin of mapped reads</p>
    <p>Display a bar chart showing the proportion of reads that align to 5' Leader (5' UTR), CDS, 3' Trailer (3' UTR), and Other (Non-coding) regions for multiple files. Typically good quality Ribo-seq files will have an significant proportion of reads aligning to CDS regions with comparitevely few aligning to UTR's and OTHER regions.</p>
    <p>This plot can use either gene level counts or transcript level counts to calculate the proportion of reads aligning to different regions, these will both give similiar results, the major difference being that transcript level counts will be broken down further with extra categories (start codons and stop codons).</p>
    <p>By changing the 'Categorise by' option to File it will instead produce a stacked bar chart with one bar per file, and each region in the bar being colored seperately.</p>

    <p class="help_subheader">Nucleotide Composition</p>
    <p>This plot shows the nucleotide composition across all selected reads in a given range. The minimum and maximum readlengths allowed to contribute to this plot can be controlled in the settings for this plot. Other settings you can change for this plot are the ability to inlcude mapped/unmapped reads. Wheter the nucleotides are taken from the 5' end or 3' end of all reads, and wheter the absolute counts are shown or percentages at each position.</p>
    <p>This plot can be used to assess if there is an unusually high number of nucleotides at a certain position in the read. For example if you see a high percentage of A's at the 3' end of unmapped reads it may indicate that polyA tails were not correctly removed from these reads, or a high percentage of T at the first position may indicate a problem with untemplated addition.</p>
    <p>In frame or out of frame (relative to the CDS) reads can also be selected. Unlike the 'All mapped reads' or 'All unmapped reads' options these are not precomputed. This means they will take longer to return a plot, it also means any sequence mismatches such as untemplated additions or untrimmed adapter will not be apparent. This may be useful to assess if nuclease bias has an affect on triplet periodicity.</p>

    <p class="help_subheader">Read Length Distribution</p>
    <p>This plot shows different read lengths on the x-axis and their counts on the y-axis. All mapped reads in a file contribute to this plot (wheter they map ambiguously or not). This plot can be used to help determine if a file contains genuine riboseq reads or not. In general we would expect the readlength distribution to have a relatively narrow peak centered around ~30 nucleotides for genuine riboseq data.</p>

    <p class="help_subheader">Heatmap</p>
    <p>This plot shows different readlengths on the y-axis and a position relative to start/stop codon on the x-axis. The data itself represents count data of reads (based on 5'/3' end of reads depending on start codon. Genuine Ribo-Seq data should have a much higher density of reads 3' of the start codon than 5' of the start codon (and vice versa for the stop codon).</p>
    <p>Other settings the user can choose are the colour palette of the heatmap, the minimum and maximum readlengths and minimum/maximum position relative to the start/stop codon, wheter to reverse the colour scale, wheter to use log values on the scale, and wheter to use a fixed value for maximum scale value (useful if comparing different files and you don't want the scale to change).</p>

    <p class="help_subheader">Correlation analysis</p>
    <p>This plot simply maps the counts for every transcript from two different files. This is useful for assessing technical/biological replicates. Ideally there should be a good correllation between replicates, showing a tight linear relationship between the two files. If this is not the case it could indicate an issue with one of the files.</p>

    <p class="help_subheader">Read length distribution across RNA region origin</p>
    <p>Display a line chart showing counts aligning to different regions of a transcript, broken down by read length.</p>

    <p class="help_subheader">Sample clustering</p>
    <p>Hierarchial clustering of samples.</p>

    <p class="help_subheader">Reads breakdown</p>
    <p>This plot shows a breakdown of reads into multiple categories for each file. Categories are as follows:</p>
    <table class="help_table">
      <tr>
        <td><strong>Unmapped:</strong></td>
        <td>Number of reads which could not be mapped to the transcriptome.</td>
      </tr>
      <tr>
        <td><strong>Ambiguous:</strong></td>
        <td>The number of reads which mapped to more than one gene.</td>
      </tr>
      <tr>
        <td><strong>Mapped noncoding:</strong></td>
        <td>Number of reads mapped to regions not annotated as coding.</td>
      </tr>
      <tr>
        <td><strong>Mapped coding:</strong></td>
        <td>Number of reads mapped to annotated coding regions.</td>
      </tr>
    </table>
    <p>The reasons for using this plot would be to assess problems with the mapping, for instance if the majority of reads were unmapped and the number of reads removed by cutadapt was very low it may indicate a problem with adapter removal. It could also be used to assess wheter a file was genuinely riboseq or not as we would expect a higher proportion of reads to align the the coding than noncoding regions compared to rnaseq data.</p>

    <p class="help_subheader">Most frequent unmapped reads</p>
    <p>This shows a sorted table of the top 100 most frequently unmapped reads in the files selected. Each read shows the number of times it appeared in the file and a link to the <a href='https://blast.ncbi.nlm.nih.gov/Blast.cgi?PAGE_TYPE=BlastSearch' target="_blank">blast</a> resource, where the read will be automatically filled into the search box. This table can be useful to detect contamination in samples.</p>
  `,
    },
    {
      id: "differential-expression",
      title: "Differential Expression help",
      description:
        "This page can be used to carry out differential expression/translation analysis using deltaTE.",
      image: differentialIcon,
      plotDescription: `
        <div class="help_video_reminder">
          This page uses drag and drop file inputs, it's recommended you view the chapter on 'Drag and drop file selection' in the
          <a href="/help/plot/help-video?t=2:23" target="_blank">help video</a> if this is your first time using this 
          plot/analysis type.<br><br>

          This page generates an interactive plot, it's recommended you view the chapter on 'Interacting with plots' in 
          the <a href="/help/plot/help-video?t=4:44" target="_blank">help video</a> if this is your first time using this plot/analysis type. 
        </div>

        <p class="help_subheader">Why use Differential Expression plots</p>

        <p>Differential expression analysis enables simultaneous comparison of 
           transcriptional and translational changes between experimental conditions. 
           By comparing RNA-seq and Ribo-seq fold changes, this plot helps distinguish 
           whether changes in translation are driven by transcriptional regulation, 
           translational regulation, or a combination of both. </p>

        <p>This makes differential expression analysis particularly useful for 
           identifying genes with altered translational efficiency, stress-responsive 
           translation, and condition-specific regulatory mechanisms that cannot be 
           detected using RNA-seq or Ribo-seq alone.

           Click <a href="https://eirnabioconnect.com/short/l2Q" target="_blank">here</a> to 
           view differential expression between two groups from the DemoArsenite study.</p>


        <p>The control group consists of untreated HEK cells while the treatment group 
           are HEK cells treated with arsenite. Each point is representative of a gene 
           and is color coded based on the regulatory classification it falls into. 
           Hovering over a specific point will enable a pop-up box displaying information
           such as fold change based on sequencing type, gene name and certain 
           differential expression parameters.</p> 

        <p>Genes of interest can also be highlighted - for instance copy and pasting the following:
           ATF4, ATF5, IFRD1, PTP4A1, HOXB2, SLC35A4 into the highlight genes input box and pressing enter will highlight 
           those genes on the plot where you will see: 
           ATF4 & IFRD1 are classified as intensified up. 
           ATF5, PTP4A1 & SLC35A4 are classified as exclusive up.
           HOXB2 is classified as non-significant.

        <p>Clicking on a gene redirects the user to a comparison plot for the selected 
           gene, showing the normalised Ribo-seq and RNA-seq profiles used in the 
           differential expression analysis. 

           Click <a href="https://eirnabioconnect.com/short/l2R" target="_blank">here</a> to 
           view the comparison for ATF4 with normalised mapped reads for RIBO-seq and 
           RNA-seq visualised as selected in Differential Expression. Here we can see 
           that the normalised mapped RNA-seq reads for the Arsenite group are higher 
           compared to the control group. This difference is further intensified in the 
           Ribo-seq profile.</p>

        <p class="help_subheader">Volcano plots</p>

        <p>Though not as informative as differential expression plots, it's also possible
           to generate volcano plots using a single seq-type. To do this simply fill in
           only the 2 Ribo-seq input boxes or only the two RNA-seq boxes and click the 
           view plot button. This can be useful if you only have 1 seq-type available or  
           want to study either transcriptional or translational changes in isolation.</p>

        <p class="help_subheader">General Plot Structure</p>

        <p>The plot title displays the fold change (log2) of one sequencing type versus 
           another (for example, Ribo-seq FC vs RNA-seq FC), with the associated 
           shortcode shown in brackets.
          
           The X-axis denotes the normalised log2 fold change for the second sequencing 
           type, while the Y-axis denotes the normalised log2 fold change for the first 
           sequencing type. A diagonal reference line indicates equal fold change 
           between sequencing types.</p>


        <p>Each gene is represented by a single color-coded dot corresponding to its 
           assigned regulatory classification. The legend indicates the regulatory 
           classification assigned to each gene based on its differential expression profile.
           To the right of the plot are navigation and image download tools, followed by
           options to share the plot via shortcode or download the read counts and 
           differential expression output in a zipped folder. Users may highlight 
           specific genes via the highlight box. Sliders are also available to adjust 
           label size and point opacity.</p>





        <p class="help_subheader">Methodology</p>
        <p>Differential gene expression is carried out using <a href="https://currentprotocols.onlinelibrary.wiley.com/doi/full/10.1002/cpmb.108" target="_blank">deltaTE (Chothani et al., 2019).</a> The approach analyses differential changes in mRNA counts, RPFs and TE (the ratio of the RPFs over mRNA counts) for each gene using the DESeq2 package. Each gene's fold-change is considered to be significant if it's determined to have a False Discovery Rate (FDR) lower than the default threshold of 5%. With the RNA-seq data the number of reads that map to the entire mRNA transcript are used while for Ribo-seq, only reads that mapped to the CDS are used for DE analysis.</p>
        
        <p>In addition to the interactive scatter plot, CSV files can be downloaded which show fold changes between conditions at the RNA-seq, Ribo-seq and TE (ratio of mean normalized Ribo‐seq counts over RNA‐seq counts) levels. Each deltaTE output consists of the following 6 columns:</p>
        
        <table class="help_table table table-bordered">
        <thead>
        <tr>
        <th>Column Header</th>
        <th>Description</th>
        </tr>
        </thead>
        <tbody>
        <tr>
        <td><strong>Gene Name</strong></td>
        <td>Gene Identifier</td>
        </tr>
        <tr>
        <td><strong>baseMean</strong></td>
        <td>The Average of the Normalized Counts taken over all Samples.</td>
        </tr>
        <tr>
        <td><strong>Log2FoldChange</strong></td>
        <td>Log 2 Fold Change between Conditions.</td>
        </tr>
        <tr>
        <td><strong>lfcSE</strong></td>
        <td>The Standard Error for the Log 2 Fold Change</td>
        </tr>
        <tr>
        <td><strong>Pvalue</strong></td>
        <td>P Values for Differences between Conditions</td>
        </tr>
        <tr>
        <td><strong>Padj</strong></td>
        <td>Adjusted P Values for Differences between Conditions</td>
        </tr>
        </tbody>
        </table>

        <p>The deltaTE output for TE also includes a Stat column which is the test statistic for the comparison.</p>

        <p>Lists of differentially transcribed genes (DTGs) and differentially translational efficiency genes (DTEGs) are also provided. These lists of genes have also been further divided into several regulatory classifications (up and down relative to control samples) as indicated in the table below:</p>

        <table class="help_table table table-bordered">
        <thead>
        <tr>
        <th>Classification</th>
        <th>RNAseq</th>
        <th>Riboseq</th>
        <th>TE</th>
        <th>DTG/DTEG</th>
        </tr>
        </thead>
        <tbody>
        <tr>
        <td><strong>Forwarded Up</strong></td>
        <td style="background-color: #d4edda;">Sig Increase</td>
        <td style="background-color: #d4edda;">Sig Increase</td>
        <td style="background-color: #fff3cd;">No Sig Change</td>
        <td>DTG Only</td>
        </tr>
        <tr>
        <td><strong>Forwarded Down</strong></td>
        <td style="background-color: #f8d7da;">Sig Decrease</td>
        <td style="background-color: #f8d7da;">Sig Decrease</td>
        <td style="background-color: #fff3cd;">No Sig Change</td>
        <td>DTG Only</td>
        </tr>
        <tr>
        <td><strong>Exclusive Up</strong></td>
        <td style="background-color: #fff3cd;">No Sig Change</td>
        <td style="background-color: #d4edda;">Sig Increase</td>
        <td style="background-color: #d4edda;">Sig Increase</td>
        <td>DTEG Only</td>
        </tr>
        <tr>
        <td><strong>Exclusive Down</strong></td>
        <td style="background-color: #fff3cd;">No Sig Change</td>
        <td style="background-color: #f8d7da;">Sig Decrease</td>
        <td style="background-color: #f8d7da;">Sig Decrease</td>
        <td>DTEG Only</td>
        </tr>
        <tr>
        <td><strong>Intensified Up</strong></td>
        <td style="background-color: #d4edda;">Sig Increase</td>
        <td style="background-color: #d4edda;">Sig Increase</td>
        <td style="background-color: #d4edda;">Sig Increase</td>
        <td>Both</td>
        </tr>
        <tr>
        <td><strong>Intensified Down</strong></td>
        <td style="background-color: #f8d7da;">Sig Decrease</td>
        <td style="background-color: #f8d7da;">Sig Decrease</td>
        <td style="background-color: #f8d7da;">Sig Decrease</td>
        <td>Both</td>
        </tr>
        <tr>
        <td><strong>Buffered Up</strong></td>
        <td style="background-color: #f8d7da;">Sig Decrease</td>
        <td></td>
        <td style="background-color: #d4edda;">Sig Increase</td>
        <td>Both</td>
        </tr>
        <tr>
        <td><strong>Buffered Down</strong></td>
        <td style="background-color: #d4edda;">Sig Increase</td>
        <td></td>
        <td style="background-color: #f8d7da;">Sig Decrease</td>
        <td>Both</td>
        </tr>
        </tbody>
        </table>

        <p>The 4 main classifications of DE may be described as follows:</p>
        <p><strong>Forwarded:</strong> Genes driven by transcriptional regulation. These genes do not have a change in TE, and the change in RNA drives the change in RPFs.</p>
        <p><strong>Exclusive:</strong> Genes regulated exclusively by translation. This means that the change in TE is driven by the change in RPFs exclusively, and there is no change in mRNA counts.</p>
        <p><strong>Intensified:</strong> Genes for which the translational regulation acts with the transcriptional regulation change. These genes have the translational change in the same direction as their transcriptional change.</p>
        <p><strong>Buffering:</strong> Genes for which the translational regulation counteracts (at least to some degree) the transcriptional regulation change. In these genes, the TE change occurs in the opposite direction to that for both the Ribo-seq and RNA-seq.</p>
     
       <div className="alert alert-light mt-3 mb-3" style={{ border: "1px solid #dee2e6" }}>
        <h6><i className="ri-file-pdf-line me-2"></i>PDF Report Format</h6>
        <p className="mb-1">The deltaTE results are also provided in a PDF format. PCA plots illustrating the separation of samples based on condition for Ribo-seq and RNA-seq normalised counts are provided in the first two pages. The third page features a scatter plot with total mRNA (RNA-seq) log2 Fold Changes between pairwise conditions shown on the x-axis and translated mRNA (Ribo-seq) log2 Fold Change between pairwise conditions shown on the y-axis. Significant genes are coloured according to the regulatory classification of their expression (Forwarded; Exclusive; Intensified; Buffered).</p>
        </div>

        `,
    },
    {
      id: "pause-prediction",
      title: "Pause Prediction help",
      description:
        "This page can be used to detect pauses in ribosome profiling and disome-seq data, either in a single condition or directly comparing two groups.",
      image: pausePredictionIcon,
      plotDescription: `

        <div class="help_video_reminder">
          This page uses both checkbox file inputs (in single mode) and drag and drop file inputs (in comparison mode), 
          it's recommended you view the chapters on 'Checkbox file selection' and 'Drag and drop file selection' in the
          <a href="/help/plot/help-video?t=0:55" target="_blank">help video</a> if this is your first time using this 
          plot/analysis type.<br><br>

          This page generates an interactive table, it's recommended you view the chapter on 'Interacting with tables' in 
          the <a href="/help/plot/help-video?t=6:58" target="_blank">help video</a> if this is your first time using this plot/analysis type. 
        </div>



        <p class="help_subheader">Plot description</p>
        <p>"Pause prediction" attempts to identify atypically high relative ribosome protected fragment (RPF) read density in transcript silhouettes. The relative read density along the transcript is significantly influenced by ribosome decoding rates, with other factors also playing a role. These additional factors include sequence-specific biases present in sequencing, potential inaccuracies in read mapping due to ambiguity, and features in translation such as alternative translation initiation events or ribosome drop-off/termination. When disregarding these other factors, it is expected that the read density at individual positions would show an inverse relationship with their decoding rates.</p>
        
        <p>Our PausePrediction tool compares the average gene density at 2 adjacent nucleotides to the background density. This background density is a trimmed average read density estimated from all positions with mapped reads.<br>
        Compared to our previous method (still available by clicking on the Use legacy mode option), the approach provides more reliable predictions for genes with low read coverage, as it addresses issues of sparse density that previously resulted in the estimation of stronger pauses.<br>
        The tool offers two distinct search functions. The "Single" function allows users to identify regions with unusually high read density pauses, while the "Comparison" function looks for pauses in two user-selected datasets. Only pauses that show differential observations (i.e., differences in presence or magnitude) are returned to the user.</p>

        <p>The output produced by Comparison analysis consist of the following columns:</p>

        <table class="help_table table table-bordered">
        <thead>
        <tr>
        <th>Column Header</th>
        <th>Description</th>
        </tr>
        </thead>
        <tbody>
        <tr>
        <td><strong>Gene:</strong></td>
        <td>Gene Identifier</td>
        </tr>
        <tr>
        <td><strong>Tran:</strong></td><td>Transcript Identifier</td>
        </tr>
        <tr>
        <td><strong>Position:</strong></td>
        <td>Transcript position of pause site</td>
        </tr>
        <tr>
        <td><strong>Region:</strong></td>
        <td>The region of the transcript the pause was predicted in, can be 5' Leader, CDS start, CDS, CDS stop, 3' Trailer or non-coding if the pause was found on a transcript that is annotated as such.</td>
        </tr>
        <tr>
        <td><strong>Upstream sequence:</strong></td>
        <td>Nucleotide Sequence of region immediately upstream of pause site</td>
        </tr>
        <tr>
        <td><strong>Downstream sequence:</strong></td>
        <td>Nucleotide Sequence of region immediately at and downstream of pause site. The pause site is at first nucleotide in the column</td>
        </tr>
        <tr>
        <td><strong>Pause 1:</strong></td>
        <td> Pause score values at transcript coordinate for Group 1</td>
        </tr>
        <tr>
        <td><strong>Pause 2:</strong></td>
        <td> Pause score values at transcript coordinate for Group 2</td>
        </tr>
        <tr>
        <td><strong>Diff Pause score:</strong></td>
        <td>Quantification of difference in Pause score. Log2((Pause 2 +3)/(Pause 1 +3)). To avoid zero values, a nominal value of 3 is included in the calculation.</td>
        </tr>
        <tr>
        <td><strong>Link:</strong></td>
        <td>This column contains a link which when clicked on will open a separate tab showing a comparison plot for the selected transcript, the predicted pause will be highlighted in yellow and all selected samples will be displayed as separate colours.</td>
        </tr>
        </tbody>
        </table>

        <p>These results can also be viewed as a CSV file by clicking the 'Download entire table' link to the top left of the interactive table. If more than one sample was selected and "Reproducible pauses only:" is clicked, the downloadable csv file will contain the aggregated result from all selected samples (the short code will be unique to that search and visible in brackets above the interactive table).
        Unclick "Reproducible pauses only" to obtain individual results for each selected sample.</p>
      `,
    },
    {
      id: "genome-browser",
      title: "Genome Browser",
      description:
        "This page can be used to view and explore genomic alignments.",
      image: genomeBrowserIcon, 
      isGroup: true,
      subPages: [
        {
          id: "single-genome-browser",
          title: "Single Genome Browser help",
          description:
            "View individual samples on the genome browser",
          image: singleGenomeIcon,
          parentTitle: "Genome Browser",
          plotDescription: `

        <div class="help_video_reminder">
          This page uses checkbox file inputs, it's recommended you view the chapter on 'Checkbox file selection' in the
          <a href="/help/plot/help-video?t=0:55" target="_blank">help video</a> if this is your first time using this 
          plot/analysis type.<br><br>

          If this is your first time using the genome browser, it's recommended you view the 'Genome browser' chapter in the 
          <a href="/help/plot/help-video?t=8:04" target="_blank">help video</a>. 
        </div>
        <p class="help_subheader">Plot description</p>
        <p>The single version of the genome browser can display each sample as its own separate track; samples cannot be grouped together. The exception is the global tracks, which aggregate all data for each unique sequence type and are always shown at the top of the track list. All samples for a particular sequence type contribute to the relevant global track regardless of whether the sample is currently displayed or not.</p>
        
        <p>Reads are colored according to the strand they map to: forward strand reads are red/orange, reverse strand reads are blue. By default, the forward strand is shown when the genome browser loads (coordinates increase left to right). You can switch to the reverse strand by clicking the 'Show Reverse' button at the top left. The coloring remains consistent regardless of strand view.</p>
        
        <p class="help_subheader">Genomic counts</p>
        <p>The reads on the subcodon profile approximately match what you see on the genome browser, but counts may differ. This is because subcodon profile samples are aligned to the transcriptome, while genome browser samples are aligned to the genome. Differences can arise, for example, in how ambiguous reads are classified.</p>
        
        <p class="help_subheader">Annotation track</p>
        <p>The annotation track is always shown at the bottom of the track list. Transcripts are colored blue for coding and red for non-coding. CDS regions are shown as thick blue bars, UTR/non-coding regions as thinner blue or red bars, and introns as thin lines connecting exons. Arrows indicate strand orientation.</p>
        
        <p>Clicking a transcript in the annotation track opens a popup with basic transcript information and a link to the subcodon profile for that transcript. This profile will load all samples if only global tracks are displayed, or a subset if individual tracks are shown.</p>
      `,
        },
        {
          id: "comparison-genome-browser",
          title: "Comparison genome browser help",
          description:
            "Compare multiple samples on the genome browser",
          image: comparisonGenomeIcon,
          parentTitle: "Genome Browser",
          plotDescription: `
        <div class="help_video_reminder">
          This page uses drag and drop file inputs, it's recommended you view the chapter on 'Drag and drop file selection' in the
          <a href="/help/plot/help-video?t=2:23" target="_blank">help video</a> if this is your first time using this 
          plot/analysis type.<br><br>

          If this is your first time using the genome browser, it's recommended you view the 'Genome browser' chapter in the 
          <a href="/help/plot/help-video?t=8:04" target="_blank">help video</a>. 
        </div>
        <p class="help_subheader">Plot description</p>
        <p>The setup of the comparison version of the genome browser is similar to the comparison profile in that multiple samples can be placed into several groups and given customisable labels and colours. Using the comparison genome browser is very similiar to the use of the single version of the genome browser, however unlike the single version of the genome browser, global tracks will not be displayed and reads will not be colored according to which strand they align to.</p>

        <p class="help_subheader">Genomic counts</p>
        <p>Reads on the comparison profile will approximately match what you see on the genome browser in most cases, but counts may not match exactly. The reason for this is that the samples you see on the genome browser and comparison profile are processed seperately with the comparison profile samples being aligned to the transcriptome and genome browser samples being aligned to the genome. This can lead to differences in counts, for example with determining if a given read is ambiguous, a read that maps to both an intronic/intergenic region and a transcript would be classed as ambiguous in the genome browser but not in the comparison profile.</p>
        
        <p>Unlike the comparison profile there is currently no normalisation options between different groups on the comparison genome browser, all tracks show raw counts.</p>

        <p class="help_subheader">Annotation track</p>
        <p>The annotation track is always shown on the bottom of the track list. Transcripts will be colored either blue (for coding) or red (for non-coding). CDS regions are displaed as the thickest blue bars, followed by UTR/non-coding regions which are displayed as thinner blue or red bars. Introns are thin blue or red lines that connect each of the exons. White arrows in the middle of exons (or red/blue arrows in the middle of introns) show the strand orientation for that transcript.</p>
        
        <p>Like the single genome browser, clicking on a transcript in the annotations will bring up a pop up with basic information for that particular transcript. Clicking the link here will open up a new tab showing the comparison profile with the same groups that were filled in on the comparison genome browser page.</p>
      `,
        },
      ],
    },
    {
      id: "translon-explorer",
      title: "Translon explorer help",
      description:
        "This page can be used to detect translons/translated ORFs.",
      image: orfTranslationIcon,
      plotDescription: `

        <div class="help_video_reminder">
          This page uses checkbox file inputs, it's recommended you view the chapter on 'Checkbox file selection' in the
          <a href="/help/plot/help-video?t=0:55" target="_blank">help video</a> if this is your first time using this 
          plot/analysis type.<br><br>

          This page generates an interactive table, it's recommended you view the chapter on 'Interacting with tables' in 
          the <a href="/help/plot/help-video?t=6:58" target="_blank">help video</a> if this is your first time using this plot/analysis type. 
        </div>


        Translon Explorer uses ribosome profiling data to identify ORFs that are likely to be translated, also known as *translons* (short for 
        <b>transl</b>ated regi<b>ons</b>). For a more in-depth explanation of the term Translon and the reason for its adoption see the following paper: 
        <a href="https://www.nature.com/articles/s41592-025-02810-3" target="_blank">nature.com/articles/s41592-025-02810-3</a><br><br>

        Translon Explorer identifies potential translons by extracting various features characteristic of translation signals, then applying a machine learning model to assign a score indicating the likelihood that a given region is translated.  

        Below is a brief description of each field in the interactive results table and the downloadable CSV file:<br><br>

        <table class="help_table">
            <tr>
                <td>Gene:</td>
                <td> The name of the gene the Translon/ORF is located on. </td>
            </tr>

            <tr>
                <td>Tran:</td>
                <td> The transcript ID the Translon/ORF is located on. </td>
            </tr>

            <tr>
                <td>Start:</td>
                <td> The start position of the Translon/ORF (in nucleotides).</td>
            </tr>

            <tr>
                <td>Stop:</td>
                <td> The stop position of the Translon/ORF (in nucleotides).</td>
            </tr>

            <tr>
                <td>Length:</td>
                <td> The length of the Translon/ORF (in nucleotides)</td>
            </tr>

            <tr>
                <td>Probability:</td>
                <td>A score between 0 and 1 representing the likelihood that this ORF is translated. Values closer to 1 indicate higher confidence.</td>
            </tr>

            <tr>
                <td>Avg. read density:</td>
                <td>The average read density across the Translon/ORF, calculated as the number of reads divided by the Translon/ORF length.</td>
            </tr>

            <tr>
                <td>Start Codon:</td>
                <td> The identity of the start codon. </td>
            </tr>

            <tr>
                <td>Type:</td>
                <td> The category of the Translon/ORF (e.g uORF, nested, dORF, etc.)</td>
            </tr>

            <tr>
                <td>Subcodon Profile:</td>
                <td> A link to the subcodon profile of the relevant transcript where the Translon/ORF in question will be highlighted.</td>
            </tr>

            <tr>
                <td>Genome Browser:</td>
                <td> A link to the genome browser where the Translon/ORF in question will be highlighted.</td>
            </tr>


        </table>

        </div>

  `,
    },
    {
      id: "differential-distribution",
      title: "Differential distribution help",
      description:
        `This links to a seperate page which lists plots related to differential 
         distribution analysis plots ('Differential silhouette' and 
         'Differential CDS/Leader').`,
      image: differentialDistributionIcon,
      isGroup: true,
      subPages: [
        {
          id: "differential-silhouette",
          title: "Differential silhouette help",
          description:
            "Identify changes in transcript silhouettes between conditions",
          image: differentialSilhouetteIcon,
          parentTitle: "Differential distribution help",
          plotDescription: `

        <div class="help_video_reminder">
          This page uses drag and drop file inputs, it's recommended you view the chapter on 'Drag and drop file selection' in the
          <a href="/help/plot/help-video?t=2:23" target="_blank">help video</a> if this is your first time using this 
          plot/analysis type.<br><br>

          This page generates an interactive plot, it's recommended you view the chapter on 'Interacting with plots' in 
          the <a href="/help/plot/help-video?t=4:44" target="_blank">help video</a> if this is your first time using this plot/analysis type. 
        </div>

        <p class="help_subheader">Why use differential silhouette plots?</p>
        <p>Here a "silhouette" refers to the distribution of reads along a transcript. 
           Differential silhouette analysis attempts to identify changes in a transcript
           silhouette between two Groups. These changes in ribosome protected fragment 
           (RPF) density profile, i.e. silhouette can arise due to variations in the 
           expression of transcript isoforms, alternative ORF (Open Reading Frame) 
           utilization, or differential pausing. Changes in sequencing depth or gene 
           expression, on the other hand, would not lead to a change in the silhouettes.</p>

        <p>Two algorithms are available to identify transcripts that exhibit 
           differential silhouettes between Groups. The first approach,"D-corr", 
           involves comparing the median pairwise Pearson's correlation coefficient 
           across Groups with that observed within replicates. This method bears 
           similarities to the one described in Andreev et al. 2015. The second 
           approach, "D-max" entails determining a cumulative sum value for an 
           aggregated silhouette of the control and test Groups. The maximum difference
           between the cumulative profiles of both Groups is then calculated. These 
           values are standardized or transformed to account for variations in the 
           sequencing depth of different genes. The approach we utilized here is a 
           modification of the one described in Lintner et al. 2017.</p>

        <p>D-max identifies genes that undergo a significant shift in ribosome density 
           either upstream or downstream in the treatment compared to the control. This
           may be expected in cases of differential ORF utilization or induction of 
           ribosome queing. D-corr, while less proficient in identifying such cases, is
           capable of detecting changes in silhouette that are not related to a shift
           in the "center of mass" of ribosome footprints. This may be because of 
           changes in ribosome elongation rates.</p>

        <p>Click <a href="https://eirnabioconnect.com/short/l2S" target="_blank">here</a> to view the Differential 
           Silhouette between two groups from the DemoLintner study. The control group 
           corresponds to untreated samples while the treatment groups represent samples
           exposed to the experimental condition used in Lintner et al 2017 
           (doi:10.1371/journal.pbio.2001882.). The observed silhouette difference 
           therefore reflects changes in the positional distribution of ribosome 
           footprints along the transcript rather than changes in overall gene 
           expression.</p>

        <p>Hovering over a point displays the D-max and D-corr measurements, the gene 
           name and transcript ID. 
           Clicking on the point (for RPL27; <a href="https://eirnabioconnect.com/short/l2W" target="_blank">here</a>, redirects 
           you to a comparison plot for the selected gene using the same groups 
           analysed in Differential Silhouette. By enabling the ‘cumulative sum’ option 
           for both groups (top right), differences in silhouette shape can be more 
           easily visualised. The D-max position toggle highlights the maximum 
           cumulative difference via dotted lines. In this example, a higher number of 
           ribosome footprints are observed in the treatment group between nucleotide 
           positions 80 - 160.</p>


        <p>The output produced by Differential silhouette analysis consist of the follow columns:</p>

        <table class="help_table table table-bordered">
          <thead>
            <tr>
            <th>Column Header</th>
            <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Gene:</strong></td>
              <td>Gene Identifier</td>
            </tr>
            <tr>
              <td><strong>Transcript:</strong></td>
              <td>Transcript Identifier</td>
            </tr>
            <tr>
              <td><strong>Total_reads:</strong></td>
              <td>The sum of raw mapped reads across all replicates in both Groups</td>
            </tr>
            <tr>
              <td><strong>Within_replicate_median_corr:</strong></td>
              <td>median of pairwise Pearson's correlation coefficients obtained for transcript silhouette within replicates</td>
            </tr>
            <tr>
              <td><strong>Between_replicate_median_corr:</strong></td>
              <td>median of pairwise Pearson's correlation coefficients obtained for transcript silhouette between samples of two Groups</td>
            </tr>
            <tr>
              <td><strong>D-corr_test_statistic:</strong></td>
              <td>test statistic from T-test for the means of two independent samples of scores.</td>
            </tr>
            <tr>
              <td><strong>D-corr_pval:</strong></td>
              <td>p-value from T-test for the means of two independent samples of scores.</td>
            </tr>
            <tr>
              <td><strong>Cumulative_sum_difference_max:</strong></td>
              <td>Maximum difference between cumulative sum of silhouette in goup 2 and Group 1</td>
            </tr>
            <tr>
              <td><strong>Cumulative_sum_difference_min:</strong></td>
              <td>Minimum difference between cumulative sum of silhouette in Group 2 and goup 1</td>
            </tr>
            <tr>
              <td><strong>Cumulative_sum_difference_max_index:</strong></td>
              <td>Transcript position of Cumulative_sum_difference_max</td>
            </tr>
            <tr>
              <td><strong>Cumulative_sum_difference_min_index:</strong></td>
              <td>Transcript position of Cumulative_sum_difference_max</td>
            </tr>
            <tr>
              <td><strong>Z-score D-max_X:</strong></td>
              <td>Absolute D-max score exceed X? X is specified by user and is 8 by default</td>
            </tr>
            <tr>
              <td><strong>Within_replicate_median_corr>Y:</strong></td>
              <td>Within_replicate_median_corr exceed Y? Y is specified by user and is 0.15 by default</td>
            </tr>
            <tr>
              <td><strong>D-corr_>Z:</strong></td>
              <td>Difference of in correlation values (Within_replicate_median_corr - Between_replicate_median_corr) exceeds Z? Z is specified by user and is 0.4 by default</td>
            </tr>
          </tbody>
        </table>

        <p class="help_subheader">General Plot Structure</p>

        <p>The plot title states ‘Differential Silhouette’ with the associated shortcode
           shown in brackets. The X-axis represents D-corr which is a unitless 
           correlation derived measure of silhouette similarity across two groups 
           (how reproducibly different are the silhouettes shapes). The Y-axis 
           represents D-max, a unitless measure of maximum difference between 
           cumulative silhouette profiles of two groups (how large is the silhouette 
           shape difference).</p>

        <p>Each gene is represented by a single color-coded dot corresponding to its 
           assigned differential classification, as indicated in the legend.<br>
           -D-corr & D-max<br>
           -D-corr only<br>
           -D-max only<br>
           To the right of the plot are navigation and image download tools, followed by
           options to share the plot via shortcode or download the differential 
           silhouette analysis output as a CSV file. Users may highlight specific genes 
           via the highlight box. Sliders are also available to adjust label size and 
           point opacity.</p>



      `,
        },
        {
          id: "diff-leader-cds",
          title: "Diff CDS/Leader help",
          description:
            "Analyze changes in CDS/5'Leader expression ratios",
          image: diffLeaderCdsIcon,
          parentTitle: "Differential distribution help", // For breadcrumb navigation
          plotDescription: `

        <div class="help_video_reminder">
          This page uses drag and drop file inputs, it's recommended you view the chapter on 'Drag and drop file selection' in the
          <a href="/help/plot/help-video?t=2:23" target="_blank">help video</a> if this is your first time using this 
          plot/analysis type.<br><br>

          This page generates an interactive plot, it's recommended you view the chapter on 'Interacting with plots' in 
          the <a href="/help/plot/help-video?t=4:44" target="_blank">help video</a> if this is your first time using this plot/analysis type. 
        </div>

        <p class="help_subheader">Why use differential CDS/Leader plots</p>
        <p>In eukaryotes the translation of ORFs in the 5'Leader is thought to play a 
           significant role with the regulation of translation of the main CDS. This 
           regulation of uORFs may be achieved by various mechanisms. Differential 
           CDS/5'Leader expression analysis attempts to identify genes which display 
           changes of mapped reads in the CDS/5'Leader between two groups and may be 
           indicative of uORF mediated regulation.</p>

        <p>Click <a href="https://eirnabioconnect.com/short/l2X" target="_blank">here</a> to
           view the DIfferential CDS/Leader plot between two groups from the 
           DemoArsenite study. The control group consists of untreated HEK cells, while 
           the treatment group was treated with arsenite.</p>

        <p>Hovering over a dot displays information such as fold change, minimum mapped 
           read count and gene name. 
           Clicking on a dot (e.g <a href="https://eirnabioconnect.com/short/l2Y" target="_blank">ATF4</a>),
           redirects to a comparison for the selected gene using the same groups analysed in 
           Differential CDS/Leader.</p>

        <p>Here, we note higher ribosomal coverage across the CDS in the treatment 
           group. This is preceded by elevated ribosomal footprints over an overlapping
           upstream open reading frame (uORF) between nucleotide position 187-364 in 
           Frame 3. This is consistent with stress-induced uORF-mediated regulation and 
           may contribute to increased translation of ATF4 under arsenite treatment.</p>

        <p class="help_subheader">General Plot Structure</p>

        <p>The plot title states ‘Diff. CDS/Leader ratio’ with the associated shortcode 
           shown in brackets. The X-axis denotes the normalised minimum number of mapped
           reads to a given gene in either the 5’ Leader or CDS region. The Y-axis is 
           the normalised fold change of read counts for that gene.

        <p>Each gene is represented by a single dotplot. Grey dots are insignificant 
           while orange dots are considered significant (Z-score > 3.5), as shown in the
           legend. To the right of the plot are navigation and image download tools, 
           followed by options to share the plot via shortcode or download the 
           Differential CDS/Leader analysis output as a CSV file. Users may highlight 
           specific genes via the highlight box. Sliders are also available to adjust 
           label size and point opacity.</p>

        <p class="help_subheader">Methodology</p>

        <p>Our tool conducts a Z-score transformation based on fold change (of the 
           CDS/5'Leader ratio) between two groups. Genes are first sorted and binned 
           based on the minimum number of mapped reads (either in 5'leader or CDS of 
           either group). A background distribution is also obtained by comparing 
           samples of the same group (i.e. replicates). The mean and standard deviation 
           of fold changes obtained from the background distribution are used to conduct
           the transformation of all the genes within the bin. This analysis is conducted
           for every pairwise comparison between two groups. The result of this analysis
           is illustrated using an MA plot.</p>
        
        <p>We generally annotate a gene as <strong>significant</strong> if the absolute
           average Z-score exceeds a value of 3.5. However genes that display high 
           variation within groups are not classified as <strong>significant</strong> 
           even if it exceeds this threshold. In addition to the interactive scatter 
           plot, CSV files can be downloaded which show the average Z-score of each 
           gene and an indication of whether it was found to be significant.</p>
      `,
        },
      ],
    },
    {
      id: "codon-occupancy",
      title: "Codon Occupancy help",
      description:
        `This page can be used to count the number of reads per codon in a set of 
         samples, or to compare two sets of samples to see the differences in the 
         number of reads per codon.`,
      image: codonOccupancyIcon,
      plotDescription: `

        <div class="help_video_reminder">
          This page uses drag and drop file inputs, it's recommended you view the chapter on 'Drag and drop file selection' in the
          <a href="/help/plot/help-video?t=2:23" target="_blank">help video</a> if this is your first time using this 
          plot/analysis type.<br><br>

          This page generates an interactive plot, it's recommended you view the chapter on 'Interacting with plots' in 
          the <a href="/help/plot/help-video?t=4:44" target="_blank">help video</a> if this is your first time using this plot/analysis type. 
        </div>

        <p class="help_subheader">Why use codon occupancy plots</p>

        <p> Codon occupancy plots visualise ribosome density at individual codons, 
            enabling detection of codon-specific translational pausing and comparison of
            elongation dynamics between conditions. They are particularly useful for 
            identifying stalls caused by amino acid limitation, stress responses, or 
            other experimental perturbations that affect translation.</p>

        <p> Differences in codon occupancy should be interpreted in the context of the 
            experimental condition, as increased occupancy may reflect induced ribosome
            stalling rather than direct effects of translation rescue or quality-control
            factors.</p>


        <p>Click <a href="https://eirnabioconnect.com/short/l30" target="_blank">here</a> to 
           view the codon occupancy plot between two groups from the DemoArsenite study. 
           The plot type selected is ‘Comparison’.


          Click <a href="https://eirnabioconnect.com/short/l31" target="_blank">here</a> to 
          view the codon occupancy plot for the control samples using the Plot Type 
          ‘Single’ option.

          Click <a href="https://eirnabioconnect.com/short/l32" target="_blank">here</a> to 
          view the codon occupancy plot for the treatment samples using the Plot Type 
          ‘Single’ option.</p>

        <p class="help_subheader">General Plot Structure</p>

        <p>The plot title displays Codon Occupancy, with the associated shortcode shown 
           in brackets. To the right of the plot are navigation and image download tools,
           followed by tabs to share the plot via shortcode or download the read counts 
           in csv format. Below these controls is a toggle to switch the Offset type 
           between P-site or A-site (default). The user is also able to highlight 
           specific codon(s) or amino acids via the highlight box.</p>



        <p><b>Plot Type : Single</b></p>

        <p>The X-axis displays codons, amino acids or Di-amino acids depending on the
           selection made at General Settings. The Y-axis displays the normalised 
           count of Ribo-seq reads mapped.</p>

        <p><b>Plot Type : Comparison</b></p>

        <p>The X-axis displays the normalised counts of Ribo-seq reads mapped for 
           samples in Group 1
           The Y-axis displays the normalised counts of Ribo-seq reads mapped for 
           samples in Group 2
           Each point then represents a specific codon, amino acid or Di-amino acid 
           (each given a specific colour) depending on the selection made at General 
           Settings.
           A diagonal reference line indicates equal occupancy between the two groups.</p> 

      `,
    },
    {
      id: "transcriptome-info",
      title: "Transcriptome info help page",
      description:
        `This page can be used to get information from the transcriptome not linked to 
         any specific dataset, for example lengths/GC content of a group of transcripts.`,
      image: transcriptomeInfoIcon,
      plotDescription: `
      <div class="help_video_reminder">
          This page uses checkbox file inputs, it's recommended you view the chapter on 'Checkbox file selection' in the
          <a href="/help/plot/help-video?t=0:55" target="_blank">help video</a> if this is your first time using this 
          plot/analysis type.<br><br>

          This page generates an interactive plot, it's recommended you view the chapter on 'Interacting with plots' in 
          the <a href="/help/plot/help-video?t=4:44" target="_blank">help video</a> if this is your first time using this plot/analysis type. 
      </div>
      <p class="help_subheader">Plot description</p>
      <p>The transcriptome info page is used to get information on sequences/annotations, no data is displayed on this page. This can be useful if you are doing your own analysis or to cross-check with results from other plot types, for example to check if genes/transcripts that are translationally upregulated have longer 5' Leaders (5' UTRs) or higher GC content. The following plot types are available on this page:</p>

      <table class="help_table">
        <tr>
          <td>Nucleotide composition (Single transcript)</td>
          <td>Used to explore the nucleotide composition of an individual transcript. Enter a transcript ID in the input box and press 'View plot', a plot will be displayed showing a sliding window across the entire transcript of individual nucleotide compositions. These can be turned on/off using the legend on the top right of the plot.</td>
        </tr>
        <tr>
          <td>Fetch sequences</td>
          <td>This can be used to generate a FASTA file based on the transcriptome of your project. 
              With default settings the FASTA will contain the entire sequence of every principal transcript isoform. 
              Setting the transcripts option to 'All' will instead return every transcript, or set to custom to enter a 
              list of genes or transcript ID's and retrieve only those. If entering gene names, the principal transcript
              isoform will be used, do not enter a mix of gene names and transcript ID's.<br><br>

              The Min/Max limits section can be used to refine which part of the sequence is returned. The default 
              anchors of 'Transcript start' and 'Transcript end' will return the entire sequence. Setting the min limit 
              anchor to 'Transcript start' and max limit anchor to 'CDS start' will return the 5'UTR/5' Leader sequence.
              Setting the min limit anchor to 'CDS start' and the
              max limit anchor to 'CDS stop' will return only the CDS sequence, etc.<br><br>

              This can be further refined by using the 'offset' inputs. 
              For example setting the min limit anchor to 'CDS start' and it's offset to -6 and the max limit anchor to 
              'CDS start' and it's offset
              to 4 will return just the sequences surrounding start codons.<br><br>

              NOTE: Setting either the min limit or max limit anchors to 'CDS start' or 'CDS stop' will ignore any 
              noncoding transcripts. This is also 
              true when using a custom transcript list, if you have noncoding transcripts in your list and either 
              anchor is set to
              'CDS start'/'CDS stop' then those transcripts will not be in the resulting FASTA, if all transcripts are 
              noncoding you will get an error.</td>
        </tr>
        <tr>
          <td>Gene count</td>
          <td>Displays the number of annotated genes and transcripts in a transcriptome. These are broken down further by the number of annotated coding (green) and non-coding (red) genes/transcripts.</td>
        </tr>
        <tr>
          <td>ORF Table</td>
          <td>Generate a table of uORFs or other ORF types with information such as start/stop positions, length and start codon. Start coordinates (including CDS starts) point to the first nucleotide of the codon, stop coordinates (including CDS stops) point to the last nucleotide of the codon. Results can also be downloaded as a csv file. This table returns a ORFs based on sequence only, not to be confused with the <strong>Translon Explorer</strong> plot type.</td>
        </tr>
        <tr>
          <td>Codon usage</td>
          <td>Displays the codon counts of coding regions of all principal isoforms if the supplied list is empty, a custom list of transcrips (comma seperated) can also be entered to only get codon counts from those transcripts. This will count codons based on sequence only, not to be confused with the <strong>Codon Occupancy</strong> plot type.</td>
        </tr>
      </table>
  `,
    },
    {
      id: "time-series",
      title: "Time series help page",
      description:
        `This page can be used to carry out differential expression/translation analysis
         using DESeq2 across a customisable number of groups.`,
      image: timeSeriesIcon,
      plotDescription: `
        <div class="help_video_reminder">
          This page uses drag and drop file inputs, it's recommended you view the chapter on 'Drag and drop file selection' in the
          <a href="/help/plot/help-video?t=2:23" target="_blank">help video</a> if this is your first time using this 
          plot/analysis type.<br><br>

          This page generates an interactive plot, it's recommended you view the chapter on 'Interacting with plots' in 
          the <a href="/help/plot/help-video?t=4:44" target="_blank">help video</a> if this is your first time using this plot/analysis type. 
        </div>
    <p>The time series/dose response plot allows you to compare changes in gene expression across multiple different conditions. This can be done at the level of a single sequence type (Ribo-Seq or RNA-Seq) or by comparing translation efficiency (TE) when you input both Ribo-Seq and RNA-Seq. It also allows you to make comparisons when you have both controls and treatments for Ribo-Seq and RNA-Seq for each different time point/dose (referred to as 'Multiple Controls' on the time series page).</p>

    <p>The resulting heatmap will be a list of genes equal to the "No. of genes displayed" option on the page, sorted by smallest adjusted p-value. These genes will be clustered so that those with similiar expression levels are grouped together. The number of heatmaps returned will depend on the seq type of the input samples, if you input a single seq type, only a single heatmap will be returned. In order to save time, inputting both Ribo-Seq and RNA-Seq will return 3 heatmaps, a TE heatmap (Ribo-seq normalised by RNA-Seq level for each gene), and a Ribo-Seq/RNA-Seq only heatmap. The Ribo-seq/RNA-Seq only heatmaps will be the same as if you had input them as a single seq type. 3 buttons will appear above the heatmap to allow you to switch between these heatmaps. The Ribo-Seq/RNA-Seq only heatmaps are independent from the TE heatmap and each other and will typically not have an identical set of genes (unless using a custom gene list), however a button to the right of the plot (labelled Match TE plot) allows you to switch to the same genes/gene order as in the TE heatmap to enable easier comparisons.</p>

    <p>The genetype option allows you to select either 'All', 'Coding', 'Non-coding' or a custom gene list. Analysis will be carried out on all genes regardless of this selection, this only controls what genes will be shown on the heatmap. If entering a custom gene list the heatmap will display all the genes in the list (irrespective of whether they were identified to be DE or not or what the 'No. of genes displayed' option is set to).</p>

    <p>Each heatmap will also show the total number of genes below the plot title that had an adjusted p-value below 5%. It is possible that not all of the significant genes will be displayed on the heatmap if this number is greater than the "No. of genes displayed" value (or if using a custom gene list). Those genes that are significant and displayed on the heatmap will have their gene names colored red on the y-axis to distinguish them from those with an adjusted p-value >5% (colored in black). You can also hover over any cell on the heatmap to see which gene it belongs to as well as its padj and Log2FC values. Clicking the link above the plot 'Download output files' will also contain excel sheets that allow you to see the padj value of each gene.</p>
  `,
    },
    {
      id: "readthrough-analysis",
      title: "Readthrough analysis help page",
      description:
        "This links to a seperate page which lists plots related to readthrough analysis.",
      image: readthroughAnalysisIcon,
      isGroup: true,
      subPages: [
        {
          id: "rrts",
          title: "RRTS help page",
          description:
            "Ribosome Read Through Score (RRTS) analysis based on Wangen et al.",
          image: rrtsIcon,
          parentTitle: "Readthrough analysis",
          plotDescription: `
          <div class="help_video_reminder">
            This page uses drag and drop file inputs, it's recommended you view the chapter on 'Drag and drop file selection' in the
            <a href="/help/plot/help-video?t=2:23" target="_blank">help video</a> if this is your first time using this 
            plot/analysis type.<br><br>

            This page generates an interactive plot, it's recommended you view the chapter on 'Interacting with plots' in 
            the <a href="/help/plot/help-video?t=4:44" target="_blank">help video</a> if this is your first time using this plot/analysis type. 
          </div>
        <p class="help_subheader">Plot description</p>
        
        <p><strong>R</strong>ibosome <strong>R</strong>ead <strong>T</strong>hrough <strong>S</strong>core (or <strong>RRTS</strong>) is based on the implementation described in <a href="https://elifesciences.org/articles/52611" target="_blank">Wangen et al.</a> with the following modifications:</p>
        
        <p>1. Only in-frame counts are taken from both the CDS region and readthrough region.<br>
        2. The count value is set to 0 for any position in the readthrough region which overlaps with the CDS of another transcript isoform.<br>
        3. The default minimum CDS density is set at 0.01, (0.001 was used in Wangen et al.), though this can be changed in the options section at the top of the page.<br>
        4. The default minimum CDS coverage is set at 30%, (no minimum coverage in Wangen et al.), though this can be changed in the options section at the top of the page.</p>

        <p>Output headers are as follows:</p>
        
        <table class="help_table">
          <tr>
            <td>Gene:</td> 
            <td>The gene name for this entry.</td>
          </tr>
          <tr>
            <td>Transcript:</td>  
            <td>The transcript ID for this entry.</td>
          </tr>
          <tr>
            <td>CDS count:</td>  
            <td>The number of in-frame reads in the CDS region (CDS start+18 nucleotides-CDS stop-15 nucleotides) between the minimum and maximum read lengths (as set in the options section at the top of the page).</td>
          </tr>
          <tr>
            <td>RT count:</td>  
            <td>The number of in-frame reads in the readthrough region (CDS stop+6 nucleotides-Next in-frame stop) between the minimum and maximum read lengths (as set in the options section at the top of the page).</td>
          </tr>
          <tr>
            <td>Stop sequence:</td>  
            <td>The stop codon identity of the CDS stop. If any nucleotides are input in the 'Context Before' or 'Context After' options at the top of the page the sequence will be extended to reflect that.</td>
          </tr>
          <tr>
            <td>CDS Length:</td>  
            <td>The length of the CDS in nucleotides (after substracting 18 nts from the beginning and 15 nts from the end of the CDS sequence).</td>
          </tr>
          <tr>
            <td>RT Length:</td>  
            <td>The length of the readthrough region in nucleotides (between CDS_stop+6 nt and the next inframe stop).</td>
          </tr>
          <tr>
            <td>CDS Density:</td>  
            <td>The CDS count divided by the CDS length.</td>
          </tr>
          <tr>
            <td>CDS Coverage:</td>  
            <td>A percentage of the number of positions in the CDS region that have at least count (in-frame only).</td>
          </tr>
          <tr>
            <td>RT Density:</td>  
            <td>The RT count divided by the RT length.</td>
          </tr>
          <tr>
            <td>RRTS:</td>  
            <td>The <strong>R</strong>ibosome <strong>R</strong>ead <strong>T</strong>hrough <strong>S</strong>core, calculated by dividing the RT density by the CDS density.</td>
          </tr>
          <tr>
            <td>(Group1 RRTS - Group2 RRTS):</td>
            <td>This is the value of the Group1 RRTS minus the Group2 RRTS, only applicable in comparison mode. The 1000 transcripts with the largest absolute Group1 RRTS - Group2 RRTS will be shown in the interactive table, but all transcripts that pass filters will be present in the downloadable csv file.</td>
          </tr>
          <tr>
            <td>View:</td>  
            <td>A link to view the subcodon plot (if in 'single' analysis mode) or comparison plot (if in 'comparison' analysis mode) of the transcript. Both the CDS and RT regions will be highligted in yellow.</td>
          </tr>
        </table>
      `,
        },
        {
          id: "utr-periodicity",
          title: "3' UTR periodicity help page",
          description:
            "Grouped bar plots showing percentage of reads in different frames in CDS and 3' UTR",
          image: utrPeriodicityIcon,
          parentTitle: "Readthrough analysis",
          plotDescription: `
        <p class="help_subheader">Plot description</p>
        
        <p>This plot type produces grouped bar plots showing the percentage of reads in the -1, 0 (In-frame), and +1 frames in both the CDS and 3' UTR.</p>
        
        <p>Each plot has a horizontal grey dotted line at y=33.3 to indicate the value at which all 3 frames are equal. By default plots are based on all coding principal transcripts, but a custom transcript list can also be supplied.</p>
        
        <p>This can be done for a single file or group of files by dragging files to only one of the input boxes, dragging files to two or more input boxes will produce a panel of plots with the group name appearing above each sub plot.</p>

        <p>If more than one file (i.e replicates) is placed in a group the resulting plot will have error bars for each region/frame to indicate the standard deviation of those replicates. The exact standard deviation value as well as the proportion of reads in a particular region/frame can be seen by hovering the cursor over one of the bars in the plot.</p>

        <p>This information can also be gotten by clicking the 'CSV' button beneath the plot.</p>
        
        <p>The CSV file will show the percentage of reads in each region/frame as well as the standard deviations first.</p>
        
        <p>Below that will be the 'Files breakdown' section which will list each file and which group it belongs to as well as the percentage of reads in each region/frame.</p>

        <p>For any transcripts where the 3'UTR overlaps with the CDS of another transcript isoform, each position in the 3'UTR is checked to see if it overlaps with an alternative CDS, if it does then counts at that specific position will be ignored (i.e taken to be zero). Any positions in the 3'UTR that do not overlap with the CDS of another transcript will still contribute to the result.</p>
      `,
        },
        {
          id: "termination-metagene",
          title: "Termination metagene help page",
          description:
            "Metagene profiles around CDS stop codons broken down by reading frame",
          image: terminationMetageneIcon,
          parentTitle: "Readthrough analysis",
          plotDescription: `
        <p class="help_subheader">Plot description</p>

        <p>Similiar to the metagene profiles in the 'Data Quality' section of EIRNA-Bio Connect, these metagene profiles are made by taking all reads from principal coding transcripts within +/- 300 nucleotides of the annotated CDS stop.</p>

        <p>Unlike the metagene profiles in the 'Data Quality' section these profiles are broken down by reading frame and coloured seperately. These metagenes also offer greater control over their creation with options to limit the region reads are taken downstream of the annotated CDS stop, a custom transcript list can also be supplied.</p>
      `,
        },
      ],
    },
    {
      id: "polysome-seq",
      title: "Polysome-seq analysis help",
      description: `Polysome-seq analysis allows you to compare changes in polysome 
                    fractions between two conditions.`,
      image: polysomeIcon,
      plotDescription: `
        <div class="plot_description">
        <p>We adapted the <strong>P</strong>olysome <strong>F</strong>raction <strong>I</strong>ndex (<strong>PFI</strong>) and <strong>P</strong>olysome <strong>S</strong>hift <strong>R</strong>atio (<strong>PSR</strong>) from Maslon MM et al 2014 study <sup>[1]</sup> and its variation from Bangru S et al 2018 study <sup>[2]</sup>.</p>
        
        <p>These metrics are calculated for each transcript. They allow the user to compare changes in polysome fractions between two conditions.</p>
        
        <p>For each transcript and each fraction, <strong>PFI</strong> is calculated as normalised reads counts in the corresponding fraction divided by the sum of normalised read counts in all fractions. For normalisation, we use TPM (transcripts per million). 'Min. Threshold' is set as 10tpm by default meaning that total TPM for a transcript over all replicates should be at least 10tpm.</p>
        
        <p><strong>PSR</strong> is a ratio of treatment and control PFIs (in log10-transformed form), it is another way of exploring the polysome fractional shift between conditions.</p>
        
        <p><strong>PFI</strong> is visualised in a form of two heatmaps, one for control and treatment (Heatmap Comparison). By clicking on 'Use Z-score:' option, the user can opt for a scaling transformation that helps enhance clusters and trends in heatmap comparison visualisation.</p>
        
        <p><strong>PSR</strong> is shown as one heatmap with ratios (Shift scores).</p>
        
        <p>In order to find statistically significant changes (option 'Show only significant hits:') in polysome fractions between conditions, we used the following procedure. Two non-overlapping subpopulations of reads from the control samples were used to calculate <strong>PSR</strong>(control/control) as a null distribution. We calculated p-value as the probability of observing <strong>PSR</strong>(treatment/control) value given that our null hypothesis is true (<strong>PSR</strong>(control/control)). Next, we applied the multiple testing correction procedure to the set of transcripts and kept FDR at 0.05.</p>
        
        <p>The output produced by Polysome-seq analysis consist of the following columns:</p>
        
        <table class="help_table table table-bordered">
        <tbody>
        <tr>
        <td><strong>Gene</strong></td>
        <td> Gene identifier</td>
        </tr>
        <tr>
        <td><strong>Transcript</strong></td>
        <td>Transcript identifier </td>
        </tr>
        <tr>
        <td><strong>Fraction</strong></td>
        <td>Polysome fraction (e.g. subpolysomal, light, heavy)</td>
        </tr>
        <tr>
        <td><strong>Control_PFI</strong></td>
        <td>Polysome fraction index for control samples </td>
        </tr>
        <tr>
        <td><strong>Treatment_PFI</strong></td>
        <td>Polysome fraction index for treatment samples </td>
        </tr>
        <tr>
        <td><strong>log10_shift</strong></td>
        <td>Polysome shift ratio (log10-transformed) </td>
        </tr>
        <tr>
        <td><strong>shift</strong></td>
        <td>Polysome shift ratio</td>
        </tr>
        <tr>
        <td><strong>pval</strong></td>
        <td>P-value from the statistical test based on comparison with null distribution (PSR(control/control))</td>
        </tr>
        <tr>
        <td><strong>adj_pvalue</strong></td>
        <td>Adjusted p-value after multiple testing correction with the Benjamini-Hochberg procedure (FDR)</td>
        </tr>
        </tbody>
        </table>

        <p><strong>References:</strong></p>
        <p>[1] Maslon MM, Heras SR, Bellora N, Eyras E, Cáceres JF. The translational landscape of the splicing factor SRSF1 and its role in mitosis. Elife. 2014 May 6;3:e02028. doi: 10.7554/eLife.02028.</p>
        <p>[2] Bangru S, Arif W, Seimetz J, Bhate A, Chen J, Rashan EH, Carstens RP, Anakk S, Kalsotra A. Alternative splicing rewires Hippo signaling pathway in hepatocytes to promote liver regeneration. Nat Struct Mol Biol. 2018 Oct;25(10):928-939. doi: 10.1038/s41594-018-0129-2. Epub 2018 Sep 24. PMID: 30250226; PMCID: PMC6173981.</p>
        </div>
      `,
    },
    {
      id: "batch-subcodon",
      title: "Batch sub-codon profiles help",
      description:
        `This page can be used to generate multiple sub-codon profiles at once, results 
         can be in PNG or HTML format, counts can also be generated in csv format. 
         Results will be returned in a zipped folder.`,
      image: batchSubcodonIcon,
      plotDescription: `
    <p class="help_subheader">Plot description</p>
    <p>The batch subcodon profile download page can be used to create many subcodon-profiles at once. The inputs are very similiar to the single subcodon profile page, except instead of entering a single transcript, there is a text box to input many at once, you can also input gene names in this box in which case the principal transcript for each gene will be used.</p>
    
    <p>For technical reasons PNG files are not available to download in batch mode. However HTML files when downloaded and opened in a browser can generate PNG images by clicking the save tool in the plots toolbar, in the same manner used to generate them in the standalone subcodon profile plot type.</p>
    
    <p><strong>Result:</strong> Instead of a single plot being returned as the result, a button will appear down the bottom of the page labelled "Download files". Clicking this button will download a zip file containing CSV or HTML files depending on the file type selected, the file types can be selected at the top left of the page.</p>
    
    <p><strong>Time:</strong> The length of time taken for the job to complete will depend on both the number of samples selected and heavily on the file types selected, CSV files are quicker to generate than HTML files. If no reads are available for a particular transcript a CSV/HTML file will not be returned, a list of such transcripts will be returned in a file labelled "_ZERO_COUNT_TRANSCRIPTS".</p>
  `,
    },
    {
      id: "batch-comparison",
      title: "Batch comparison profiles help",
      description:
        `This page can be used to generate multiple comparison profiles at once, results
         can be in PNG or HTML format, counts can also be generated in csv format. 
         Results will be returned in a zipped folder.`,
      image: batchComparisonIcon,
      plotDescription: `
        <p class="help_subheader">Plot description</p>
        <p>The batch comparison profile download page can be used to create many comparison profiles at once. The inputs are very similiar to the single comparison profile page, except instead of entering a single transcript, there is a text box to input many at once, you can also input gene names in this box in which case the principal transcript for each gene will be used. Transcript ID's and gene names cannot be mixed.</p>
        
        <p>For technical reasons PNG files are not available to download in batch mode. However HTML files when downloaded and opened in a browser can generate PNG images by clicking the save tool in the plots toolbar, in the same manner used to generate them in the standalone comparison profile plot type.</p>
        
        <p><strong>Result:</strong> Instead of a single plot being returned as the result, a button will appear down the bottom of the page labelled "Download files". Clicking this button will download a zip file containing CSV or HTML files depending on the file type selected, the file types can be selected at the top left of the page.</p>
        
        <p><strong>Time:</strong> The length of time taken for the job to complete will depend on both the number of samples selected and heavily on the file types selected, CSV files are quicker to generate than HTML files. If no reads are available for a particular transcript a CSV/HTML file will not be returned, a list of such transcripts will be returned in a file labelled "_ZERO_COUNT_TRANSCRIPTS".</p>
      `,
    },
  ],
};
